import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { UUID } from "crypto";
import type { PreOrder } from "@/app/types";
import { format } from "date-fns";
import { partnerKeys } from "../../keys/partners";
import { preOrderKeys } from "./keys";
import { cachePreOrderRelatedData, cachePreOrders } from "./cacheHelpers";
import { useOfflineRecapPreOrders } from "./offlineShim";
import {
  computeUTCRangeInTz,
  useCompanyTimezone,
} from "@/lib/datetime/companyTimezone";

/**
 * Hook to fetch ALL pre-orders for the current user
 * Useful for CRM, analytics, and comprehensive views
 *
 * Note: Backend limits page size to max 1000 (see backend/app/utils/pagination.py)
 */
export function useAllPreOrders(pageOptions = { page: 1, size: 1000 }) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.list(pageOptions.page, pageOptions.size),
    queryFn: async () => {
      const result = await api.preOrders.getAllPreOrders(pageOptions);
      // Cache pre-orders and their related entities (partners, etc.)
      cachePreOrders(queryClient, result.items);
      return result;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch pre-orders by date
 */
export function usePreOrdersByDate(date: Date | string) {
  const dateStr = typeof date === "string" ? date : format(date, "yyyy-MM-dd");
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.byDate(dateStr),
    queryFn: async () => {
      const result = await api.preOrders.getPreOrdersByDate(dateStr);
      // Cache pre-orders and their related entities (partners, etc.)
      cachePreOrders(queryClient, result.items);
      return result;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: () => (document.hidden ? false : 2 * 60_000),
    refetchIntervalInBackground: false,
  });
}

/**
 * Delta fetch interval: 15 seconds for near-real-time updates.
 * Full refetch interval: 10 minutes to catch deletions and ensure consistency.
 * Delta buffer: 5 seconds subtracted from server_time to avoid missing
 * records that were committed between the DB query and the timestamp capture.
 */
const DELTA_INTERVAL_MS = 15_000;
const FULL_REFETCH_INTERVAL_MS = 10 * 60_000;
const DELTA_BUFFER_MS = 5_000;

/**
 * Hook to fetch pre-orders recap with delta polling.
 *
 * - Every 15s: delta fetch (only pre-orders/flows modified since last server_time - 5s)
 * - Every 10min: full refetch to catch deletions and ensure consistency
 * - Deletion detection: if total_count < cached count → immediate full refetch
 * - Merges delta results into cached array by replacing matched IDs
 */
export function usePreOrdersRecap(
  date: Date | string,
  dateField: "delivery" | "order" | "all" = "delivery",
  // includeStocks / includePartnerAccounts kept on the signature for source
  // compatibility — both supplemental REST features are unused by the
  // offline-first shim. Stocks come through the global stocks cache, partner
  // accounts are deferred until the V2 partner-accounts endpoint lands.
  _includeStocks: boolean = false,
  _includePartnerAccounts: boolean = false,
  start?: string,
  end?: string,
  openEnded?: boolean,
  endDate?: string,
  includeDeleted: boolean = false,
  enabled: boolean = true,
  // Optional business-category gate forwarded to the offline shim so /recap
  // (category=0) and /purchases (category=1) each get their own oldest-2000
  // cap budget. When omitted (e.g. mobile callers), the legacy date-only
  // compound is used — no behavior change.
  category?: 0 | 1,
) {
  // Offline-first: read pre-orders + flows from Dexie via @stockline/web,
  // filter by date client-side, return the same legacy shape downstream
  // recap consumers expect. Replaces the 15s delta poll on /api/pre-orders/
  // recap/* — SSE keeps Dexie fresh, the shim re-renders reactively.
  return useOfflineRecapPreOrders({
    date,
    dateField,
    start,
    end,
    openEnded,
    endDate,
    includeDeleted,
    category,
    enabled,
  });
}

/**
 * Hook to fetch pre-orders by WhatsApp chat UUID
 * @param chatId - WhatsApp chat UUID
 */
export function usePreOrdersByChat(
  chatId: string | null,
  pageOptions = { page: 1, size: 50 },
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.byChat(chatId!),
    queryFn: async () => {
      const result = await api.preOrders.getPreOrdersByChat(
        chatId!,
        pageOptions,
      );
      // Cache pre-orders and their related entities
      cachePreOrders(queryClient, result.items);
      return result;
    },
    enabled: !!chatId,
  });
}

/**
 * Hook to fetch pre-orders for multiple chats in a single batch request
 * Optimized for rendering chat lists with pre-order badges
 */
export function usePreOrdersByChatsBatch(chatIds: string[]) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...preOrderKeys.all, "batch", [...chatIds].sort().join(",")],
    queryFn: async () => {
      const result = await api.preOrders.getPreOrdersByChatsBatch(chatIds);

      // Cache individual pre-orders for each chat. Per-chat status-count
      // seeding lives in the global usePreOrderStatusCounts query — the
      // legacy per-chat keys are gone, so don't write to them here.
      Object.values(result)
        .flat()
        .forEach((preOrder) => {
          queryClient.setQueryData(
            preOrderKeys.detail(preOrder.id as UUID),
            preOrder,
          );
          cachePreOrderRelatedData(queryClient, preOrder);
        });

      return result;
    },
    enabled: chatIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch pre-orders for multiple SMS chats in a single batch request
 * Optimized for rendering SMS chat lists with pre-order badges
 */
export function usePreOrdersBySMSChatsBatch(chatIds: string[]) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...preOrderKeys.all, "sms-batch", chatIds.sort().join(",")],
    queryFn: async () => {
      const result = await api.preOrders.getSMSPreOrdersByChatsBatch(chatIds);

      // Cache individual pre-orders for each chat. Per-chat status-count
      // seeding lives in the global usePreOrderStatusCounts query.
      Object.values(result)
        .flat()
        .forEach((preOrder) => {
          queryClient.setQueryData(
            preOrderKeys.detail(preOrder.id as UUID),
            preOrder,
          );
          cachePreOrderRelatedData(queryClient, preOrder);
        });

      return result;
    },
    enabled: chatIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch pre-orders by SMS chat ID
 */
export function usePreOrdersBySMS(
  chatId: string | null,
  pageOptions = { page: 1, size: 50 },
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.bySMSChat(chatId!),
    queryFn: async () => {
      const result = await api.preOrders.getSMSPreOrdersByChatId(
        chatId!,
        pageOptions,
      );
      // Cache pre-orders and their related entities
      cachePreOrders(queryClient, result.items);
      return result;
    },
    enabled: !!chatId,
  });
}

// Legacy hook — kept only for the frozen `recap/mobile/_legacy` surface
// (mobile recap pre-rewrite). New code MUST use `usePreOrdersForEmail` from
// `@/query/hooks/usePreOrdersForChat` (V2 API).
export function usePreOrdersByEmail(
  emailId: string | null,
  pageOptions = { page: 1, size: 50 },
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.byEmailId(emailId!),
    queryFn: async () => {
      const result = await api.preOrders.getEmailPreOrdersByEmailId(
        emailId!,
        pageOptions,
      );
      cachePreOrders(queryClient, result.items);
      return result;
    },
    enabled: !!emailId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch email pre-orders
 */
export function useEmailPreOrders(pageOptions = { page: 1, size: 50 }) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: preOrderKeys.byEmail(),
    queryFn: async () => {
      const result = await api.preOrders.getEmailPreOrders(pageOptions);
      // Cache pre-orders and their related entities
      cachePreOrders(queryClient, result.items);
      return result;
    },
  });
}

/**
 * Hook to fetch pre-orders for a specific partner with flows
 * This is optimized for CRM views - fetches all partner pre-orders with their flows
 */
export function usePartnerPreOrders(partnerId: UUID | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...preOrderKeys.all, "partner", partnerId],
    queryFn: async () => {
      // Fetch all pre-orders
      const result = await api.preOrders.getAllPreOrders({
        page: 1,
        size: 1000,
      });

      // Filter to only this partner's pre-orders
      const partnerPreOrders = result.items.filter(
        (po: any) => po.partner_id === partnerId,
      );

      // Fetch flows for each pre-order in parallel
      const preOrdersWithFlows = await Promise.all(
        partnerPreOrders.map(async (preOrder: any) => {
          try {
            const flows = await api.preOrders.getPreOrderFlows(preOrder.id);
            return {
              ...preOrder,
              flows: flows || [],
            };
          } catch (error) {
            console.error(
              `Failed to fetch flows for pre-order ${preOrder.id}:`,
              error,
            );
            return {
              ...preOrder,
              flows: [],
            };
          }
        }),
      );

      // Cache individual pre-orders with their flows
      preOrdersWithFlows.forEach((preOrder) => {
        queryClient.setQueryData(
          preOrderKeys.detail(preOrder.id as UUID),
          preOrder,
        );
        if (preOrder.flows) {
          queryClient.setQueryData(
            preOrderKeys.flows(preOrder.id),
            preOrder.flows,
          );
        }
        // Cache partner data if available
        if (preOrder.partner) {
          queryClient.setQueryData(
            partnerKeys.detail(preOrder.partner.id),
            preOrder.partner,
          );
        }
      });

      return preOrdersWithFlows;
    },
    enabled: !!partnerId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: () => (document.hidden ? false : 2 * 60_000),
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single pre-order
 * Uses cache-first approach - checks if already cached before API call
 */
export function usePreOrder(
  id: UUID | null,
  options?: { includeDeleted?: boolean },
) {
  const queryClient = useQueryClient();
  const includeDeleted = options?.includeDeleted ?? false;

  return useQuery({
    queryKey: preOrderKeys.detail(id!),
    queryFn: async () => {
      if (!id) throw new Error("usePreOrder: id is required");
      // First check if already cached
      const cachedPreOrder = queryClient.getQueryData<PreOrder>(
        preOrderKeys.detail(id),
      );

      if (cachedPreOrder) {
        return cachedPreOrder;
      }

      // Check all cached pre-order lists
      const allPreOrderQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: preOrderKeys.all });

      for (const query of allPreOrderQueries) {
        const data = query.state.data as any;

        // Handle array responses (like byDate)
        if (Array.isArray(data)) {
          const cached = data.find((po: PreOrder) => po.id === id);
          if (cached) return cached;
        }

        // Handle paginated responses (like byChat, bySMS)
        if (data?.items && Array.isArray(data.items)) {
          const cached = data.items.find((po: PreOrder) => po.id === id);
          if (cached) return cached;
        }
      }

      // Fallback to API
      const preOrder = await api.preOrders.getPreOrder(id, { includeDeleted });

      // Cache related entities
      cachePreOrderRelatedData(queryClient, preOrder);

      return preOrder;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch change history for a pre-order.
 * Only fires when enabled (e.g. when popover is open).
 *
 * Always refetches on mount so reopening the info popover after
 * a mutation never shows a stale audit trail. `gcTime: 0` keeps the
 * entry out of IndexedDB persistence — stale data must never survive
 * a page reload either.
 */
export function usePreOrderHistory(
  preOrderId: string,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: preOrderKeys.history(preOrderId),
    queryFn: () => api.preOrders.getHistory(preOrderId),
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to fetch pre-order flows
 */
export function usePreOrderFlows(preOrderId: string | null) {
  return useQuery({
    queryKey: preOrderKeys.flows(preOrderId!),
    queryFn: () => api.preOrders.getPreOrderFlows(preOrderId!),
    enabled: !!preOrderId,
  });
}

/**
 * Hook to fetch aggregated product totals from all company pre-orders for a date.
 * This aggregates across ALL company pre-orders regardless of user permissions.
 *
 * @param date - Date to fetch product totals for
 * @param dateField - Whether to filter by "delivery", "order", or "all" date
 */
export function useProductTotals(
  date: Date | string,
  dateField: "delivery" | "order" | "all" = "delivery",
  start?: string,
  end?: string,
) {
  const dateStr = typeof date === "string" ? date : format(date, "yyyy-MM-dd");
  const tz = useCompanyTimezone();

  // Convert tz-local time range to UTC+0 for correct DB filtering
  const utcRange =
    dateField === "order" || dateField === "all"
      ? computeUTCRangeInTz(dateStr, tz, start, end)
      : undefined;

  return useQuery({
    queryKey: [
      "product-totals",
      dateStr,
      dateField,
      ...(utcRange ? [utcRange.startUTC, utcRange.endUTC] : []),
    ],
    queryFn: async () => {
      return api.preOrders.getProductTotals(
        dateStr,
        dateField,
        utcRange?.startUTC,
        utcRange?.endUTC,
      );
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
